import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchOrderById } from '../lib/api';
import { buildOrderTracking } from '../lib/orderTracking';
import { subscribeToOrder } from '../lib/orderFirestore';
import { clearTrackedOrder, isActiveOrder, writeTrackedOrder } from '../lib/trackedOrder';
import './orderStatus.css';

function OrderProgressStepper({ tracking }) {
  if (!tracking?.steps?.length) {
    return null;
  }

  return (
    <div className="order-progress-section">
      <h2 className="order-progress-title">Order Progress</h2>
      <p className="order-progress-message">{tracking.message}</p>
      <ol className="order-progress-stepper">
        {tracking.steps.map((step) => (
          <li key={step.key} className={`order-progress-step order-progress-step--${step.state}`}>
            <div className="order-progress-marker" aria-hidden="true">
              {step.state === 'completed' ? '✓' : step.state === 'current' ? '●' : '○'}
            </div>
            <div className="order-progress-content">
              <span className="order-progress-label">{step.label}</span>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

const persistTrackedOrder = (nextOrder) => {
  if (isActiveOrder(nextOrder)) {
    writeTrackedOrder(nextOrder);
  } else {
    clearTrackedOrder();
  }
};

const OrderStatusPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!orderId) {
      setError('No order ID provided');
      setLoading(false);
      return undefined;
    }

    let ignore = false;
    let unsubscribe = null;

    const loadWithApiFallback = async () => {
      try {
        const data = await fetchOrderById(orderId);
        if (!ignore) {
          setOrder(data);
          persistTrackedOrder(data);
          setError('');
          setLoading(false);
        }
      } catch (err) {
        if (!ignore) {
          setError(err.message || 'Unable to load order details');
          setLoading(false);
        }
      }
    };

    const startListener = async () => {
      try {
        unsubscribe = await subscribeToOrder(
          orderId,
          (data) => {
            if (!ignore) {
              setOrder(data);
              persistTrackedOrder(data);
              setError('');
              setLoading(false);
            }
          },
          async () => {
            if (!ignore) {
              await loadWithApiFallback();
            }
          }
        );
      } catch {
        if (!ignore) {
          await loadWithApiFallback();
        }
      }
    };

    startListener();

    return () => {
      ignore = true;
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [orderId]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'status-completed';
      case 'processing':
        return 'status-processing';
      case 'pending':
        return 'status-pending';
      case 'cancel':
        return 'status-cancelled';
      default:
        return 'status-pending';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'processing':
        return 'In Progress';
      case 'pending':
        return 'Pending';
      case 'cancel':
        return 'Cancelled';
      default:
        return 'Unknown';
    }
  };

  const formattedOrderDate = order?.created_at
    ? new Date(order.created_at).toLocaleString()
    : 'N/A';

  const tracking = order ? buildOrderTracking(order) : null;

  if (loading) {
    return (
      <div className="order-status-page">
        <div className="order-status-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading order details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="order-status-page">
        <div className="order-status-container">
          <div className="error-message">
            <h2>Unable to Load Order</h2>
            <p>{error}</p>
            <button onClick={() => navigate('/')} className="btn-back">
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="order-status-page">
        <div className="order-status-container">
          <div className="error-message">
            <h2>Order Not Found</h2>
            <p>The order you're looking for doesn't exist.</p>
            <button onClick={() => navigate('/')} className="btn-back">
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="order-status-page">
      <div className="order-status-container">
        <div className="order-header">
          <h1>Order Status</h1>
          <p className="order-id">Order #{order.id}</p>
        </div>

        {/* Admin Message Section */}
        {order.admin_message && (
          <div className="admin-message-section">
            <div className="message-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
              </svg>
            </div>
            <div className="message-content">
              <h3>Message from Vemmac Kitchen</h3>
              <p>{order.admin_message}</p>
            </div>
          </div>
        )}

        {/* Order Progress Stepper */}
        <OrderProgressStepper tracking={tracking} />

        {/* Order Status Card */}
        <div className="status-card">
          <div className="status-indicator">
            <div className={`status-badge ${getStatusColor(order.status)}`}>
              <span className="status-dot"></span>
              {tracking?.current_step || getStatusText(order.status)}
            </div>
          </div>

          <div className="status-details">
            <div className="detail-row">
              <span className="detail-label">Customer Name</span>
              <span className="detail-value">{order.customer_name || 'N/A'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Phone</span>
              <span className="detail-value">{order.phone || 'N/A'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Address</span>
              <span className="detail-value">{order.address || 'N/A'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Payment Method</span>
              <span className="detail-value">{order.payment_method || 'N/A'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Order Date</span>
              <span className="detail-value">{formattedOrderDate}</span>
            </div>
          </div>
        </div>

        {/* Order Items */}
        <div className="order-items-section">
          <h2>Order Details</h2>
          <div className="items-list">
            {order.items && order.items.length > 0 ? (
              order.items.map((item, index) => (
                <div key={index} className="item-card">
                  <div className="item-info">
                    <span className="item-name">
                      {item.qty || 1}x {item.name}
                    </span>
                    {item.add_ons && item.add_ons.length > 0 && (
                      <span className="item-addons">
                        Add-ons: {item.add_ons.join(', ')}
                      </span>
                    )}
                    {item.details && (
                      <span className="item-note">Details: {item.details}</span>
                    )}
                  </div>
                  <span className="item-price">
                    ₦{Number(item.unit_price || 0).toLocaleString()}
                  </span>
                </div>
              ))
            ) : (
              <p className="no-items">No items in this order</p>
            )}
          </div>

          <div className="order-total">
            <span className="total-label">Total Amount</span>
            <span className="total-value">
              ₦{Number(order.total || 0).toLocaleString()}
            </span>
          </div>
        </div>

        <div className="action-buttons">
          <button onClick={() => navigate('/')} className="btn-home">
            Back to Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderStatusPage;
